import { StateMachine } from "@/core/arch/state_machine";
import type { BillingRepository } from "./billing_repository";
import type { BillingAccountRow, BillingEventRow } from "./types";

export interface BillingPanelState {
  status: "idle" | "loading" | "loaded" | "error";
  /** `null` once loaded means the merchant has no projection row yet. */
  account: BillingAccountRow | null;
  events: BillingEventRow[];
  nextToken: string | null;
  loadingMore: boolean;
  error?: string;
}

const INITIAL: BillingPanelState = {
  status: "idle",
  account: null,
  events: [],
  nextToken: null,
  loadingMore: false,
};

export class BillingPanelBloc extends StateMachine<BillingPanelState> {
  /**
   * Sequences in-flight work so only the newest generation may commit.
   *
   * StrictMode replays the mount effect, so two `load()` calls are routinely in
   * flight at once; without this a slower first response can land after a faster
   * second, and a transient failure on either can turn a successful load into an
   * error screen. Pagination reads the token rather than bumping it, so a fresh
   * load also discards a page request it has made obsolete.
   */
  private _loadToken = 0;

  constructor(
    private repo: BillingRepository,
    private businessId: string,
  ) {
    super(INITIAL);
  }

  /**
   * Fetches the projection row and the first page of history together.
   *
   * Both in one round trip's worth of wall clock because neither depends on the
   * other — the timeline is keyed on the business, not on the account row, so a
   * merchant with no projection can still have history worth reading.
   */
  async load(): Promise<void> {
    const token = ++this._loadToken;
    this.setState({ ...INITIAL, status: "loading" });
    try {
      const [account, page] = await Promise.all([
        this.repo.getAccount(this.businessId),
        this.repo.listEvents(this.businessId),
      ]);

      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;

      this.setState({
        status: "loaded",
        account,
        events: page.items,
        nextToken: page.nextToken,
        loadingMore: false,
      });
    } catch (e) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;

      this.setState({
        ...INITIAL,
        status: "error",
        error: e instanceof Error ? e.message : "Failed to load billing",
      });
    }
  }

  /**
   * Appends the next page of history.
   *
   * A failure here leaves the loaded timeline in place and surfaces the message
   * rather than discarding what the operator is already reading.
   */
  async loadMore(): Promise<void> {
    const { nextToken, loadingMore } = this.state;
    if (!nextToken || loadingMore) return;

    const token = this._loadToken;
    this.setState({ ...this.state, loadingMore: true, error: undefined });
    try {
      const page = await this.repo.listEvents(this.businessId, nextToken);

      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;

      this.setState({
        ...this.state,
        events: [...this.state.events, ...page.items],
        nextToken: page.nextToken,
        loadingMore: false,
      });
    } catch (e) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;

      this.setState({
        ...this.state,
        loadingMore: false,
        error: e instanceof Error ? e.message : "Failed to load more history",
      });
    }
  }
}
